import React, { createContext, useContext } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, ButtonBase, Card, CardHeader, Collapse, Divider, Typography } from '@mui/material'
import clsx from 'clsx'
import { useStyles } from '../styles'

/*
 * How a layout wants its sections drawn, so no section has to thread a prop
 * through. With no provider (desktop, tests) every section is an open outlined
 * card. The mobile page provides `flat: true` — no card chrome, the content
 * sits straight on the page — plus which sections fold and which are open:
 *
 *   { flat, isCollapsible(id), isExpanded(id), toggle(id) }
 */
export const ProfileCardLayoutContext = createContext(null)

const HEADING_PROPS = { component: 'h2', variant: 'h6', fontWeight: 700 }

/*
 * A section drawn straight on the page (the flat* rules in ../styles.js).
 */
const FlatSection = ({ id, headingId, title, dataTest, disableGutters, layout, children, ...rest }) => {
  const classes = useStyles()
  const collapsible = Boolean(id && layout.isCollapsible(id))
  const expanded = !collapsible || layout.isExpanded(id)
  const bodyId = id ? `${id}-body` : undefined
  const body = disableGutters ? children : <Box className={classes.flatBody}>{children}</Box>

  if (!collapsible) {
    // Self-padded rows bring their own top spacing, so the heading adds none;
    // gutter content has no padding of its own above, so the heading supplies it.
    return (
      <Box component='section' id={id} aria-labelledby={headingId} data-test={dataTest} {...rest}>
        <Typography
          {...HEADING_PROPS}
          id={headingId}
          className={clsx(classes.flatHeading, disableGutters && classes.flatHeadingTight)}
        >
          {title}
        </Typography>
        {body}
      </Box>
    )
  }

  return (
    <Box component='section' id={id} aria-labelledby={headingId} data-test={dataTest} {...rest}>
      {/*
        * A disclosure: the heading holds the button, so the section is still
        * labelled by its title and the button's name is the title.
        */}
      <Typography {...HEADING_PROPS} id={headingId} className={classes.flatToggleHeading}>
        <ButtonBase
          onClick={() => layout.toggle(id)}
          aria-expanded={expanded}
          aria-controls={bodyId}
          data-test={dataTest ? `${dataTest}-toggle` : undefined}
          className={classes.flatToggle}
        >
          <span>{title}</span>
          <ExpandMoreIcon className={clsx(classes.flatToggleIcon, expanded && classes.flatToggleIconExpanded)} />
        </ButtonBase>
      </Typography>
      {/*
        * Children stay mounted while folded so their state (the privacy
        * notice's open sections, an edit in progress) survives a toggle.
        */}
      <Collapse in={expanded} id={bodyId}>
        {body}
      </Collapse>
    </Box>
  )
}

/**
 * The Account Settings section shell: outlined card, heading, divider.
 *
 * Every section hand-rolled this same Card + CardHeader before, which is how
 * they drifted apart. Rendered as a `section` labelled by its own heading, so
 * each one is a landmark a screen reader can jump to.
 */
export const ProfileCard = ({
  id,
  title,
  dataTest,
  /* Sections that manage their own edge-to-edge rows (tables, divided lists). */
  disableGutters = false,
  children,
  ...rest
}) => {
  const layout = useContext(ProfileCardLayoutContext)
  const headingId = id ? `${id}-heading` : undefined

  if (layout?.flat) {
    return (
      <FlatSection
        id={id}
        headingId={headingId}
        title={title}
        dataTest={dataTest}
        disableGutters={disableGutters}
        layout={layout}
        {...rest}
      >
        {children}
      </FlatSection>
    )
  }

  return (
    <Card
      component='section'
      variant='outlined'
      id={id}
      aria-labelledby={headingId}
      data-test={dataTest}
      sx={({ radius }) => ({
        borderRadius: `${radius.lg}px`,
        width: '100%',
        // The theme clips every Card (`overflow: hidden`) to keep
        // absolutely-positioned children inside the rounded corners. These
        // sections have none, and they do host popups — the language
        // Autocomplete among them — so clipping only risks cutting one off.
        overflow: 'visible'
      })}
      {...rest}
    >
      <CardHeader
        title={title}
        titleTypographyProps={{ ...HEADING_PROPS, id: headingId }}
        sx={{ px: 3, py: 2 }}
      />
      <Divider />
      {disableGutters ? children : <Box sx={{ p: 3 }}>{children}</Box>}
    </Card>
  )
}

export default ProfileCard
